<!--
  COMPONENTE RAÍZ DE LA APLICACIÓN - App.vue

  Este es el componente principal que contiene toda la aplicación.
  En Vue.js, los archivos .vue tienen 3 secciones:
  - <template>: HTML con sintaxis de Vue
  - <script>: JavaScript con lógica del componente
  - <style>: CSS específico del componente
-->

<template>
  <!--
    div principal que contiene toda la aplicación
    Las clases de Tailwind CSS definen el layout base:
    - min-h-screen: altura mínima de toda la pantalla
    - bg-gray-50: fondo gris claro
    - flex, flex-col: layout flexbox vertical
  -->
  <div id="app" class="min-h-screen bg-gray-50 flex flex-col">


    <!--
      CONTENIDO PRINCIPAL
      router-view es donde Vue Router renderiza los componentes de cada ruta
      Es como un placeholder que cambia según la URL actual
    -->
    <main class="flex-1">
      <!--
        Suspense maneja la carga asíncrona de componentes
        Mientras carga, muestra el contenido del slot #fallback
      -->
      <Suspense>
        <router-view />

        <!-- Fallback mientras carga el componente -->
        <template #fallback>
          <div class="flex items-center justify-center min-h-screen">
            <div class="text-center">
              <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-hanami-500 mx-auto"></div>
              <p class="mt-4 text-gray-600">Cargando...</p>
            </div>
          </div>
        </template>
      </Suspense>
    </main>

    <!--
      FOOTER (solo en desarrollo para info útil)
      v-if con $env.DEBUG solo muestra esto en modo desarrollo
    -->
    <footer v-if="$env.DEBUG" class="bg-gray-800 text-white text-xs p-2 text-center">
      Desarrollo: {{ $env.API_BASE_URL }} | Vue {{ vueVersion }}
    </footer>

  <GlobalAlerts />

  </div>
</template>

<script>
/**
 * LÓGICA DEL COMPONENTE APP
 *
 * En Vue 3 usamos la Composition API que es más flexible
 * que la Options API de Vue 2
 */

// Imports necesarios
import { computed } from 'vue'      // Para propiedades calculadas reactivas
import { useAuthStore } from '@/stores/auth'  // Store de autenticación
import { version as vueVersion } from 'vue'   // Versión de Vue para debug
import GlobalAlerts from '@/components/common/GlobalAlerts.vue' // Importar el componente de alertas

export default {
  name: 'App',  // Nombre del componente (útil para debugging)
  components: {
    GlobalAlerts // Registrar el componente
  },
  setup() {
    // Store de autenticación (Pinia)
    const authStore = useAuthStore()

    /**
     * COMPUTED PROPERTIES
     *
     * Son propiedades que se recalculan automáticamente
     * cuando sus dependencias cambian
     */

    // Función para obtener clases CSS según el rol del usuario
    const getRoleBadgeClass = computed(() => (role) => {
      const baseClasses = 'font-medium text-xs uppercase tracking-wider'

      switch (role) {
        case 'admin':
          return `${baseClasses} bg-red-100 text-red-800`
        case 'supervisor':
          return `${baseClasses} bg-blue-100 text-blue-800`
        case 'cajero':
          return `${baseClasses} bg-green-100 text-green-800`
        default:
          return `${baseClasses} bg-gray-100 text-gray-800`
      }
    })

    /**
     * RETORNO DEL SETUP
     *
     * Todo lo que retornamos aquí estará disponible en el template
     */
    return {
      authStore,            // Store de autenticación
      vueVersion,           // Versión de Vue para el footer
      getRoleBadgeClass     // Función para clases de roles
    }
  }
}
</script>

<style>
/**
 * ESTILOS GLOBALES
 *
 * Estos estilos se aplican a toda la aplicación
 * Usamos principalmente Tailwind CSS, pero podemos agregar
 * estilos personalizados aquí si es necesario
 */

/* Fuentes personalizadas si las necesitamos */
body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Estilo para elementos con focus (accesibilidad) */
button:focus,
input:focus,
select:focus,
textarea:focus {
  outline: 2px solid #059669;
  outline-offset: 2px;
}

/* Animaciones suaves para transiciones */
* {
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out;
}

/* Estilo para el scroll en dispositivos webkit (Chrome, Safari) */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>